import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { storeReturnPath, hasLocalTokens, login } from 'utils/authUtils';
import { ROUTES } from 'utils/constants';
import { useQuickLinksRecentRecorder } from 'hooks/useQuickLinksRecentRecorder';
import { Loader2 } from 'lucide-react';
import styles from './ProtectedRoute.module.scss';

function QuickLinksRecentRecorder() {
  useQuickLinksRecentRecorder();
  return null;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const isLoading = useAuthStore((state) => state.isLoading);
  const isAuth = useAuthStore((state) => state.isAuth);
  const [hasLocalTokensState, setHasLocalTokensState] = useState<
    boolean | null
  >(null);

  // Check if current route is a POS route (should work offline)
  const isPOSRoute = useMemo(() => {
    return (
      location.pathname.startsWith(ROUTES.POS.ROOT) ||
      location.pathname === ROUTES.POS.PAYMENT ||
      location.pathname === ROUTES.POS.SUCCESS
    );
  }, [location.pathname]);

  // Check for local tokens when on POS route
  useEffect(() => {
    if (isPOSRoute) {
      setHasLocalTokensState(null);
      void (async () => {
        const hasLocal = await hasLocalTokens();
        setHasLocalTokensState(hasLocal);
      })();
    } else {
      setHasLocalTokensState(null);
    }
  }, [isPOSRoute]);

  // Redirect to auth server if not authenticated
  // Store current path so we can redirect back after login
  useEffect(() => {
    if (isPOSRoute) {
      if (hasLocalTokensState === false) {
        const currentPath = location.pathname;
        storeReturnPath(currentPath);
        void login();
      }
      return;
    }

    if (
      !isLoading &&
      !isAuth &&
      location.pathname !== ROUTES.AUTH_CALLBACK &&
      location.pathname !== ROUTES.HOME
    ) {
      const currentPath = location.pathname;
      storeReturnPath(currentPath);
      void login();
    }
  }, [isAuth, isLoading, location.pathname, isPOSRoute, hasLocalTokensState]);

  // Show loading state while checking initial authentication status
  // For POS routes, also show loading while checking for local tokens
  if (isLoading || (isPOSRoute && hasLocalTokensState === null)) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.loadingIcon} size={48} />
        <p className={styles.loadingText}>Loading Authentication...</p>
      </div>
    );
  }

  if (isPOSRoute) {
    if (hasLocalTokensState === false) {
      return null;
    }
    return (
      <>
        <QuickLinksRecentRecorder />
        {children}
      </>
    );
  }

  if (!isAuth) {
    return null;
  }

  return (
    <>
      <QuickLinksRecentRecorder />
      {children}
    </>
  );
}

export default ProtectedRoute;
